import { useParams } from "react-router";
import { AddDesignerFileLayout } from "../layout";
import { DesignBoardContainer } from "./design-board-container";
import { useGetMoodBoardQuery } from "../../../hooks/use-get-mood-board-query";
import { useRef, useEffect, useState } from "react";
import { useDashboardFooter } from "../../../contexts/DashboardFooterContext";
import { Button, Stack } from "@mui/material";

export const AddDesignBoardView = () => {
  const params = useParams();
  const isEditMode = !!params?.id;
  const title = isEditMode ? "Edit Design Board" : "Add a New Design Board";
  const { getMoodBoard, loading, moodBoard } = useGetMoodBoardQuery();
  const { setFooter, clearFooter } = useDashboardFooter();
  const handleSubmitRef = useRef<(() => void) | null>(null);
  const handlePublishRef = useRef<((nextPublished: boolean) => void) | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (!hasFetched.current && isEditMode) {
      hasFetched.current = true;
      getMoodBoard(params.id as string, true).catch((error) => {
        console.error("Error fetching project file:", error);
      });
    }
  }, [isEditMode, params.id, getMoodBoard]);

  useEffect(() => {
    const handleSave = async () => {
      try {
        await handleSubmitRef.current?.();
      } catch (error) {
        console.error("Error saving design board:", error);
      }
    };

    const handlePublishToggle = async () => {
      try {
        await handlePublishRef.current?.(!isPublished);
      } catch (error) {
        console.error("Error publishing design board:", error);
      }
    };

    const footerContent = (
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2} sx={{ width: "100%" }}>
        <Button
          onClick={handlePublishToggle}
          variant="outlined"
          disabled={!isFormValid}
          sx={{
            borderColor: "#000",
            color: "#000",
            px: 4,
            "&:hover": {
              borderColor: "#333",
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "&:disabled": {
              borderColor: "#ccc",
              color: "#666",
            },
          }}
        >
          {isPublished ? "Unpublish" : "Publish"}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isFormValid}
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            px: 4,
            "&:hover": {
              backgroundColor: "#333",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
              color: "#666",
            },
          }}
        >
          {isEditMode ? "Update Design Board" : "Add Design Board"}
        </Button>
      </Stack>
    );

    setFooter(footerContent);
    return () => clearFooter();
  }, [setFooter, clearFooter, isFormValid, isEditMode, isPublished]);

  return (
    <AddDesignerFileLayout
      title={title}
      subTitle="Design a concept board with colors, patterns, or room layouts."
    >
      {(!isEditMode || !loading) && (
        <DesignBoardContainer
          moodBoard={moodBoard}
          handleSubmitRef={handleSubmitRef}
          handlePublishRef={handlePublishRef}
          onPublishStateChange={setIsPublished}
          onSetFormValid={setIsFormValid}
        />
      )}
    </AddDesignerFileLayout>
  );
};
